/**
 * CurrentUserContext — provides the current Dexie Cloud user to the React tree.
 *
 * Wraps `useObservable(db.cloud.currentUser)` at the AppShell level so
 * every child component can access user info without subscribing individually.
 *
 * Porting note: v1 accesses `db.cloud.currentUser` directly in many places.
 * v2 centralizes this into a single observable → context to minimize
 * subscriptions and re-renders.
 *
 * @see Fas 31 in view-and-dnd-tasks.md
 */

import { createContext, useContext, useMemo, type ReactNode } from "react"
import { useObservable } from "dexie-react-hooks"
import { db } from "@/db/schema"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CurrentUser {
  /** Dexie Cloud user ID (e.g. "usr...") */
  userId: string
  /** User email address */
  email: string
  /** Display name (falls back to email prefix) */
  name: string
  /** Whether the user is logged in */
  isLoggedIn: boolean
  /** Raw access token (for API calls — prefer apiClient which handles this) */
  accessToken: string | undefined
  /** License type from Dexie Cloud */
  license: string | undefined
}

interface CurrentUserContextValue {
  user: CurrentUser
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const DEFAULT_USER: CurrentUser = {
  userId: "",
  email: "",
  name: "",
  isLoggedIn: false,
  accessToken: undefined,
  license: undefined,
}

const CurrentUserContext = createContext<CurrentUserContextValue>({
  user: DEFAULT_USER,
})

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Turn "bennie.forss" or "bennie_forss" into "Bennie Forss" */
function prettifyName(raw: string): string {
  return raw
    .split(/[._-]/)
    .map((part) => (part.length > 0 ? part[0]!.toUpperCase() + part.slice(1) : ""))
    .join(" ")
    .trim()
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function CurrentUserProvider({ children }: { children: ReactNode }) {
  const dexieUser = useObservable(db.cloud.currentUser)

  const user = useMemo<CurrentUser>(() => {
    if (!dexieUser?.userId) return DEFAULT_USER

    const email = (dexieUser.email as string) ?? ""
    const rawName = (dexieUser.name as string) || ""
    // If no real display name (or name equals email), derive from email prefix
    const baseName =
      rawName && rawName !== email
        ? rawName
        : email.split("@")[0] || ""
    // Prettify: "bennie.forss" → "Bennie Forss", keep proper names as-is
    const displayName = prettifyName(baseName)

    return {
      userId: dexieUser.userId ?? "",
      email,
      name: displayName,
      isLoggedIn: !!dexieUser.accessToken,
      accessToken: dexieUser.accessToken as string | undefined,
      license: (dexieUser.license as { type?: string })?.type,
    }
  }, [dexieUser])

  // Stable reference — prevents all context consumers from re-rendering
  // when the provider re-renders but the user object hasn't changed.
  const contextValue = useMemo<CurrentUserContextValue>(
    () => ({ user }),
    [user],
  )

  return (
    <CurrentUserContext.Provider value={contextValue}>
      {children}
    </CurrentUserContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Access the current Dexie Cloud user.
 *
 * Must be used inside `<CurrentUserProvider>`.
 *
 * @example
 * const { user } = useCurrentUser()
 * if (user.isLoggedIn) { ... }
 */
export function useCurrentUser(): CurrentUserContextValue {
  return useContext(CurrentUserContext)
}
